import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { askGPT } from './OpenAIService';
import { sendEmail } from './EmailService';
import { X, Loader2,Send, AlertTriangle, Sparkles } from 'lucide-react';

const generateAIMessageAPI = async (competence) => {
  const prompt = `
    Tu es un assistant pédagogique enthousiaste de la plateforme Aladinn.
    Génère un message d'invitation personnel et engageant pour partager une expérience : ${competence.title}  avec un autre utilisateur.

    Le message doit :
    1. Être chaleureux et personnel ,
    2. Expliquer pourquoi c'est intéressant et important
    3. Évoquer l'aspect interactif et collaboratif 
    4. Se terminer par une invitation à rejoindre Aladinn
    5. Inclure des émojis appropriés
    6. Être concis (70 mots maximum)
  `;

  try {
    const response = await askGPT(prompt);
    if (!response) throw new Error('Erreur lors de la génération du message');
    return response;
  } catch (error) {
    throw new Error('Impossible de générer le message personnalisé');
  }
};

const useAIMessage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateMessage = useCallback(async (competence) => {
    if (!competence) {
      setError('Compétence non définie');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const message = await generateAIMessageAPI(competence);
      return message;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    generateMessage,
    loading,
    error
  };
};

const ShareCompetence = ({
  open,
  onClose,
  competence,
  userId,
  menuCollapsed = false
}) => {
  const location = useLocation();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [shareError, setShareError] = useState('');
  
  const { 
    generateMessage, 
    loading: messageLoading, 
    error: messageError 
  } = useAIMessage();

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (open && competence) {
      setRecipientEmail('');
      setShareError('');
      generateAIMessage();
    }
  }, [open, competence]);

  const generateAIMessage = async () => {
    if (!competence) {
      setShareError('Aucune compétence sélectionnée');
      return;
    }

    try {
      const message = await generateMessage(competence);
      if (message) {
        setCustomMessage(message);
      } else {
        setShareError('Le message n\'a pas pu être généré');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du message:', error);
      setShareError('Erreur lors de la génération du message automatique');
    }
  };

  const handleShareSubmit = async () => {
    if (!recipientEmail) {
      setShareError('Veuillez entrer un email valide.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      setShareError('Veuillez entrer une adresse email valide.');
      return;
    }

    if (!competence) {
      setShareError('Aucune compétence sélectionnée pour le partage.');
      return;
    }

    setEmailLoading(true);
    setShareError('');

    try {
      await sendEmail(recipientEmail, `${competence.title} - Invitation Aladinn`, customMessage);
      onClose();
    } catch (error) {
      setShareError('Erreur lors de l\'envoi de l\'email.');
      console.error('Erreur email:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  if (!open) return null;

  const menuWidth = menuCollapsed ? 64 : 256;
  const contentWidth = `calc(55% - 64px)`;
  const leftPosition = `calc(${menuWidth}px + ((100% - ${menuWidth}px - (${contentWidth})) / 2))`;

  return (
    <>
      <div 
        className="fixed inset-0 bg-white" 
        style={{ 
          left: menuWidth,
          top: 64,
          zIndex: 10
        }} 
      />
      
      <div 
        className="fixed top-16 bottom-0 bg-white shadow-xl rounded-t-xl overflow-hidden"
        style={{
          width: contentWidth,
          left: leftPosition,
          zIndex: 20
        }}
      >
        <div className="h-full flex flex-col">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-t-xl">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                <h2 className="text-lg font-semibold truncate">
                  {competence ? `Partager : ${competence.title}` : 'Partager une compétence'}
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/10 p-1.5"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6 space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="space-y-4">          
                  {messageLoading ? (
                    <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-gray-600">Création du message personnalisé...</span>
                    </div>
                  ) : (
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      className="w-full p-4 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[250px] resize-y whitespace-pre-line"
                      placeholder="Le message d'invitation personnalisé apparaîtra ici..."
                    />
                  )}
                </div>
              </div>

              {(shareError || messageError) && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-red-600 text-sm">
                    {shareError || messageError}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-white border-t">
            <div className="flex items-center justify-between gap-4">
              <input
                type="email"
                placeholder="Email du destinataire"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="flex-1 p-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleShareSubmit}
                disabled={emailLoading || !competence || messageLoading}
                className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
                  emailLoading || !competence || messageLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {emailLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Envoyer l'invitation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareCompetence;